import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import * as Types from '../types/types';
import { ApiClient } from 'services/api-client';
import { AuthState } from './authTypes';
import { clearStoredAuth, getStoredAuthData, storeAuthData } from './authStorage';
import { isTokenExpired } from './authUtils';

async function handleExpiredToken(
  stored: ReturnType<typeof getStoredAuthData>,
  refreshResult: Types.AuthResponse | null,
  apiClient: ApiClient,
  setAuthState: Dispatch<SetStateAction<AuthState>>,
  createGuestSession: () => Promise<boolean>
): Promise<void> {
  if (!refreshResult) {
    console.log('Token refresh failed, creating new guest session...');
    clearStoredAuth();
    apiClient.clearAuthToken();
    await createGuestSession();
    return;
  }

  const isGuest = stored.userType === 'guest';
  storeAuthData(refreshResult, isGuest);
  apiClient.setAuthToken(refreshResult.accessToken);
  setAuthState({
    user: isGuest ? null : refreshResult.user,
    guest: isGuest ? (refreshResult.user as Types.Guest) : null,
    isAuthenticated: true,
    isGuest,
    isLoading: false,
    isInitializing: false,
    error: null,
    mfaResponse: null,
  });
}

export function useAuthLifecycle(): {
  authState: AuthState;
  setAuthState: Dispatch<SetStateAction<AuthState>>;
  apiClient: ApiClient;
  refreshAccessToken: (refreshToken: string) => Promise<Types.AuthResponse | null>;
  createGuestSession: () => Promise<boolean>;
  resetGuestCreationAttempt: () => void;
} {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    guest: null,
    isAuthenticated: false,
    isGuest: false,
    isLoading: false,
    isInitializing: true,
    error: null,
    mfaResponse: null,
  });

  const [apiClient] = useState(() => new ApiClient());
  const initializationCompleted = useRef(false);
  const guestCreationAttempted = useRef(false);

  const refreshAccessToken = useCallback(
    async (refreshToken: string): Promise<Types.AuthResponse | null> => {
      try {
        return await apiClient.refreshToken(refreshToken);
      } catch (error) {
        console.error('Token refresh failed:', error);
        return null;
      }
    },
    [apiClient]
  );

  const resetGuestCreationAttempt = useCallback(() => {
    guestCreationAttempted.current = false;
  }, []);

  const createGuestSession = useCallback(async (): Promise<boolean> => {
    if (guestCreationAttempted.current) {
      return false;
    }

    guestCreationAttempted.current = true;

    try {
      const guestAuth = await apiClient.createGuestSession();
      if (!guestAuth || !guestAuth.user || guestAuth.user.userType !== 'guest') {
        return false;
      }

      storeAuthData(guestAuth, true);
      apiClient.setAuthToken(guestAuth.accessToken);
      setAuthState({
        user: null,
        guest: guestAuth.user as Types.Guest,
        isAuthenticated: true,
        isGuest: true,
        isLoading: false,
        isInitializing: false,
        error: null,
        mfaResponse: null,
      });
      return true;
    } catch (error) {
      console.error('Failed to create guest session:', error);
      guestCreationAttempted.current = false;
      setAuthState(prev => ({
        ...prev,
        user: null,
        guest: null,
        isAuthenticated: false,
        isGuest: false,
        isLoading: false,
        isInitializing: false,
        error: 'Failed to create guest session',
      }));
      return false;
    }
  }, [apiClient]);

  const initializeAuth = useCallback(async () => {
    if (initializationCompleted.current) {
      return;
    }

    try {
      const stored = getStoredAuthData();
      if (!stored.accessToken || !stored.refreshToken || !stored.userData) {
        await createGuestSession();
        return;
      }

      if (stored.userType === 'guest' && stored.userData) {
        try {
          const response = await fetch(`${apiClient.getBaseUrl()}/users/${stored.userData.id}`, {
            headers: { Authorization: `Bearer ${stored.accessToken}` },
          });
          if (!response.ok) {
            clearStoredAuth();
            await createGuestSession();
            return;
          }
        } catch {
          clearStoredAuth();
          await createGuestSession();
          return;
        }
      }

      if (isTokenExpired(stored.accessToken)) {
        const refreshResult = await refreshAccessToken(stored.refreshToken);
        await handleExpiredToken(
          stored,
          refreshResult,
          apiClient,
          setAuthState,
          createGuestSession
        );
        return;
      }

      const isGuest = stored.userType === 'guest';
      apiClient.setAuthToken(stored.accessToken);
      setAuthState({
        user: isGuest ? null : stored.userData,
        guest: isGuest ? (stored.userData as Types.Guest) : null,
        isAuthenticated: true,
        isGuest,
        isLoading: false,
        isInitializing: false,
        error: null,
        mfaResponse: null,
      });
    } catch (error) {
      console.error('Error initializing auth:', error);
      clearStoredAuth();
      apiClient.clearAuthToken();
      await createGuestSession();
    } finally {
      initializationCompleted.current = true;
    }
  }, [apiClient, createGuestSession, refreshAccessToken]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    if (!authState.isAuthenticated) {
      return;
    }

    const stored = getStoredAuthData();
    if (!stored.expiresAt) {
      return;
    }

    const timeUntilExpiry = stored.expiresAt * 1000 - Date.now() - 5 * 60 * 1000;
    if (timeUntilExpiry <= 0) {
      initializeAuth();
      return;
    }

    const refreshTimer = setTimeout(() => {
      initializeAuth();
    }, timeUntilExpiry);

    return () => clearTimeout(refreshTimer);
  }, [authState.isAuthenticated, initializeAuth]);

  return {
    authState,
    setAuthState,
    apiClient,
    refreshAccessToken,
    createGuestSession,
    resetGuestCreationAttempt,
  };
}
